import { Fragment, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { parseISO } from 'date-fns'

import { formatAsDateTimeWithTimezone } from '~/utils/time'
import { getBrowserTimezone } from '~/utils/helpers'
import { useGetReceiptsListQuery } from '~/redux/apis/payment-api'

import Pagination from '~/shared/components/Pagination/Pagination'

import ReceiptItem from './ReceiptItem'
import ReceiptPriceItem from './ReceiptPriceItem'
import { generateReceiptProductLabel } from './StudentReceipts.utils'

const RESULTS_COUNT = 10

export default function StudentReceipts(): React.ReactElement {
  const { t } = useTranslation(['generics', 'booking'])
  const timezone = getBrowserTimezone()

  const [page, setPage] = useState(0)
  const { data } = useGetReceiptsListQuery({ page, range: RESULTS_COUNT })

  const receiptsCount = data?.total || 0
  const totalPages = Math.ceil(receiptsCount / RESULTS_COUNT)

  return (
    <Fragment>
      <div className="space-y-4 flex flex-col">
        <div className="text-sm font-semibold">{t('booking:numberOfReceipts', { receipts: receiptsCount })}</div>
        <Pagination
          currPage={page}
          onDecreasePageClick={() => setPage((prevState) => prevState - 1)}
          onIncreasePageClick={() => setPage((prevState) => prevState + 1)}
          onSetPageClick={setPage}
          totalPages={totalPages}
        />

        {data?.results.map((elem) => {
          return (
            <div className="max-w-sm p-4 space-y-4 rounded-small shadow-large bg-white" key={elem.orderId}>
              <header className="flex justify-between">
                <span className="uppercase tracking-wide text-md font-medium">{t('generics:receipt')}</span>
                <span className="uppercase text-xxs text-gray-light-fade">
                  {t('generics:orderId')}: #<span>{elem.orderId}</span>
                </span>
              </header>

              <section className="space-y-2">
                <ReceiptItem label={generateReceiptProductLabel(elem, t)} title={t('generics:product')} />
                <ReceiptItem
                  label={t(`booking:paymentType.${elem.paymentType}` as const)}
                  title={t('generics:status')}
                />
                <ReceiptItem
                  label={formatAsDateTimeWithTimezone(parseISO(elem.createdAt), timezone)}
                  title={t('generics:purchasedAt')}
                />
              </section>
              <div className="h-px w-full bg-black/10" />

              <section className="space-y-2">
                <div>
                  {elem.subTotal !== 0 && (
                    <ReceiptPriceItem cost={elem.subTotal} currency={elem.currency} label={t('generics:subTotal')} />
                  )}

                  {elem.travelExpense !== 0 && (
                    <ReceiptPriceItem
                      cost={elem.travelExpense}
                      currency={elem.currency}
                      label={t('generics:travelExpense')}
                    />
                  )}
                </div>

                <ReceiptPriceItem cost={elem.priceTotal} currency={elem.currency} label={t('generics:total')} />
              </section>
            </div>
          )
        })}
      </div>
    </Fragment>
  )
}
